// Resources (D113 phase 2, APS-462; Console D, the mock's #/perf/resources): memory per screen and
// per day, memory warnings and out of memory exits per release, install size and app data, free
// space on the device, battery drain per screen, and the thermal state. The parts come from
// different sessions, and each section says which: every session's summary and screen views, or
// captured sessions' snapshots and storage records (family `resources`). One app's release in
// scope (or its latest) against the one before.
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  memoryDailyQuery,
  resourceBatteryQuery,
  resourceScreensQuery,
  resourcesQuery,
  resourceThermalQuery,
} from '../api/queries'
import type { ResourcesRelease } from '../api/types'
import { EmptyNote, Pill, Section, Strip, type StripItem, Table } from '../components/d'
import { LineChart } from '../components/LineChart'
import {
  Delta,
  NoApp,
  PerfHead,
  perfMeta,
  ReleaseLink,
  usePerfReleases,
  usePerfScope,
  withRelease,
} from '../components/Perf'
import { PLATFORM_LABEL } from '../components/Shell'
import { ErrorState, Loading } from '../components/states'
import { type Translator, useI18n } from '../i18n'
import { percentSpaced } from '../lib/format'
import { RESOURCES_SINCE, resourcesSdk } from '../lib/signals'
import { useProjectParams } from './params'

/** A p90 this far over the baseline's (MB) reads as a warning pill, as the mock's +82 MB. */
const RISE_PILL_MB = 50
/** The free space buckets, smallest first, with the mock's tones. */
const FREE = [
  ['under_1_gb', 'rs-c'],
  ['1_to_5_gb', 'rs-w'],
  ['5_to_20_gb', ''],
  ['over_20_gb', ''],
] as const

/** Bytes as whole megabytes (the mock's unit), or a dash. */
/** The platforms in the order the console names them. */
const PLATFORMS = ['android', 'ios', 'react_native'] as const

export function mb(i18n: Translator, bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—'
  return `${i18n.number(bytes / 1e6, { maximumFractionDigits: bytes < 1e8 ? 1 : 0 })} MB`
}

/** The release row of `release`, if the overview has it. */
const rowOf = (rows: ResourcesRelease[] | undefined, release: string | undefined) =>
  release === undefined ? undefined : rows?.find((r) => r.release === release)

export function ResourcesPage() {
  const i18n = useI18n()
  const { t, number, date } = i18n
  const percent = (share: number, digits: number) => percentSpaced(i18n, share, digits)
  const two = (v: number) => number(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const { orgId, projectId } = useProjectParams()
  const { scope, app, appsPending } = usePerfScope()
  const { current, previous } = usePerfReleases(scope)
  const enabled = !!scope.app_id
  // The release the page reads: the one in scope, or the app's latest, against the one before.
  const release = scope.release ?? current
  const inRelease = withRelease(scope, release)
  const overview = useQuery({ ...resourcesQuery(projectId, scope), enabled })
  const screens = useQuery({
    ...resourceScreensQuery(projectId, inRelease, previous),
    enabled: enabled && !!release,
  })
  const battery = useQuery({
    ...resourceBatteryQuery(projectId, inRelease, previous),
    enabled: enabled && !!release,
  })
  const thermal = useQuery({
    ...resourceThermalQuery(projectId, inRelease),
    enabled: enabled && !!release,
  })
  const list = screens.data?.screens ?? []
  const worst = [...list].sort((a, b) => (b.p90 ?? 0) - (a.p90 ?? 0))[0]
  const daily = useQuery({
    ...memoryDailyQuery(projectId, scope, worst?.screen),
    enabled: enabled && !!worst,
  })
  const title = t('perf.resources.title')
  const page = t('perf.nav.resources')
  const params = { orgId, projectId }

  if (appsPending)
    return (
      <main id="main" className="pg pf-pg rs-pg">
        <Loading />
      </main>
    )
  if (!app)
    return (
      <main id="main" className="pg pf-pg rs-pg">
        <PerfHead title={title} page={page} />
        <NoApp />
      </main>
    )

  const android = app.platform === 'android'
  // Spec 7.7, 7.9: while the app's SDK does not record the resources family (APS-462), the
  // captured-only sections are not drawn.
  const label = PLATFORM_LABEL[app.platform]
  const notYet = resourcesSdk(t, app.platform, label).since === null ? label : null
  const meta = perfMeta(i18n, app, withRelease(scope, release), [])
  const one = (v: number) => number(v, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  const rows = overview.data?.releases
  const cur = rowOf(rows, release)
  const was = rowOf(rows, previous)
  const vs = (now: number | null | undefined, before: number | null | undefined) =>
    now !== null && now !== undefined && before !== null && before !== undefined
      ? now - before
      : null

  const strip: StripItem[] = [
    {
      dt: t('perf.resources.strip.memory'),
      dd: worst?.p90 != null ? number(worst.p90 / 1e6, { maximumFractionDigits: 0 }) : '—',
      small: worst?.p90 != null ? 'MB' : undefined,
      d: worst ? (
        <span translate="no">
          {worst.baseline_p90 != null && previous
            ? t('perf.resources.strip.memoryVs', {
                screen: worst.screen,
                release: previous,
                before: mb(i18n, worst.baseline_p90),
              })
            : worst.screen}
        </span>
      ) : undefined,
    },
    {
      dt: t('perf.resources.strip.warnings'),
      dd: cur ? one(cur.warnings_per_1k_sessions) : '—',
      small: cur ? t('health.per1k') : undefined,
      d:
        cur && was && previous ? (
          <>
            <Delta
              value={vs(cur.warnings_per_1k_sessions, was.warnings_per_1k_sessions)}
              format={one}
            />{' '}
            {t('perf.resources.vs', {
              release: previous,
              before: one(was.warnings_per_1k_sessions),
            })}
          </>
        ) : undefined,
    },
    {
      dt: t('perf.resources.strip.oom'),
      dd: cur ? one(cur.out_of_memory_per_1k_sessions) : '—',
      small: cur ? t('health.per1k') : undefined,
      d: (
        <>
          {cur && was && previous ? (
            <>
              <Delta
                value={vs(cur.out_of_memory_per_1k_sessions, was.out_of_memory_per_1k_sessions)}
                format={one}
                bad
              />{' '}
              {t('perf.resources.vsShort', { release: previous })}
              {' · '}
            </>
          ) : null}
          {t('perf.resources.exits')}
        </>
      ),
      link: ({ className, children }) => (
        <Link
          className={className}
          to="/o/$orgId/p/$projectId/health/exits"
          params={params}
          search={{ app: scope.app_id }}
        >
          {children}
        </Link>
      ),
    },
    {
      dt: t('perf.resources.strip.install'),
      dd:
        cur?.install_size != null
          ? number(cur.install_size / 1e6, { maximumFractionDigits: 1 })
          : '—',
      small: cur?.install_size != null ? 'MB' : undefined,
      d:
        cur?.install_size != null && was?.install_size != null && previous ? (
          <>
            <Delta
              value={(cur.install_size - was.install_size) / 1e6}
              format={(v) => `${number(v, { maximumFractionDigits: 1 })} MB`}
            />{' '}
            {t('perf.resources.vsShort', { release: previous })}
          </>
        ) : undefined,
    },
  ]

  const failed = [overview, screens].find((q) => q.isError)
  return (
    <main id="main" className="pg pf-pg rs-pg">
      <PerfHead
        title={title}
        page={page}
        meta={meta}
        actions={
          <>
            {worst ? (
              <Link
                className="btn"
                to="/o/$orgId/p/$projectId/performance/screens/detail"
                params={params}
                search={{ app: scope.app_id, screen: worst.screen }}
                translate="no"
              >
                {worst.screen}
              </Link>
            ) : null}
            {release && previous ? (
              <Link
                className="btn"
                to="/o/$orgId/p/$projectId/releases/$release"
                params={{ ...params, release }}
                search={{ app: scope.app_id }}
                translate="no"
              >
                {t('perf.resources.compare', { release, base: previous })}
              </Link>
            ) : null}
          </>
        }
      />
      {overview.isPending || (release && screens.isPending) ? (
        <Loading />
      ) : failed ? (
        <ErrorState error={failed.error} retry={() => failed.refetch()} />
      ) : (
        <>
          <Strip items={strip} translate="no" />

          <Section
            title={t('perf.resources.screens')}
            meta={t('perf.resources.screensSub', { release: release ?? '—' })}
            right={
              <Link
                className="btn ghost sm"
                to="/o/$orgId/p/$projectId/performance/screens"
                params={params}
                search={{ app: scope.app_id }}
              >
                {t('perf.resources.allScreens')}
              </Link>
            }
          >
            {list.length === 0 ? (
              <EmptyNote title={t('perf.resources.screensNone')}>
                {t('perf.resources.screensNoneBody')}
              </EmptyNote>
            ) : (
              <Table
                label={t('table.of', { name: t('perf.resources.screens') })}
                className="rs-tbl"
              >
                <thead>
                  <tr>
                    <th scope="col">{t('vitals.col.screen')}</th>
                    <th className="num" scope="col">
                      {t('perf.resources.col.loads')}
                    </th>
                    <th className="num" scope="col" translate="no">
                      p50
                    </th>
                    <th className="num" scope="col" translate="no">
                      p90
                    </th>
                    <th className="num" scope="col">
                      {previous
                        ? t('perf.resources.col.vs', { release: previous })
                        : t('perf.resources.col.vsNone')}
                    </th>
                    <th className="num" scope="col">
                      {t('perf.resources.col.warnings')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[...list]
                    .sort((a, b) => (b.p90 ?? 0) - (a.p90 ?? 0))
                    .map((s) => {
                      const rise =
                        s.p90 != null && s.baseline_p90 != null
                          ? (s.p90 - s.baseline_p90) / 1e6
                          : null
                      return (
                        <tr key={s.screen} className={s === worst ? 'cur' : undefined}>
                          <td className="mono anywhere" translate="no">
                            <Link
                              to="/o/$orgId/p/$projectId/performance/screens/detail"
                              params={params}
                              search={{ app: scope.app_id, screen: s.screen }}
                            >
                              {s.screen}
                            </Link>
                          </td>
                          <td className="num">{number(s.loads)}</td>
                          <td className="num">{mb(i18n, s.p50)}</td>
                          <td className="num">{mb(i18n, s.p90)}</td>
                          <td className="num">
                            {rise === null ? (
                              <span className="faint">—</span>
                            ) : rise >= RISE_PILL_MB ? (
                              <Pill tone="warn">{`+${number(rise, { maximumFractionDigits: 0 })} MB`}</Pill>
                            ) : (
                              <Delta
                                value={rise}
                                format={(v) => `${number(v, { maximumFractionDigits: 0 })} MB`}
                              />
                            )}
                          </td>
                          <td className="num">{one(s.warnings_per_1k_loads)}</td>
                        </tr>
                      )
                    })}
                </tbody>
              </Table>
            )}
          </Section>

          <div className="grid2">
            <Section
              title={
                worst
                  ? t('perf.resources.daily', { screen: worst.screen })
                  : t('perf.resources.dailyAll')
              }
              meta={t('perf.resources.dailySub', { days: scope.days })}
            >
              <div className="pf-chart">
                {!worst ? (
                  <EmptyNote title={t('perf.resources.screensNone')} />
                ) : daily.isPending ? (
                  <Loading />
                ) : daily.isError ? (
                  <ErrorState error={daily.error} retry={() => daily.refetch()} />
                ) : (
                  (() => {
                    const ofRelease = (r: string | undefined) =>
                      daily.data.releases.find((x) => x.release === r)
                    const main = ofRelease(release) ?? daily.data.releases[0]
                    const other = daily.data.releases.find((x) => x !== main)
                    const label = (day: string) =>
                      date(`${day}T00:00:00Z`, { month: 'short', day: 'numeric', timeZone: 'UTC' })
                    return main ? (
                      <LineChart
                        label={t('perf.resources.dailyLabel', {
                          screen: worst.screen,
                          days: daily.data.days.length,
                        })}
                        name={main.release}
                        legend
                        boxLegend
                        timeAxis
                        xName={t('chart.day')}
                        points={daily.data.days.map((d, i) => ({
                          label: label(d),
                          value: main.p90[i] != null ? (main.p90[i] as number) / 1e6 : null,
                        }))}
                        more={
                          other
                            ? [
                                {
                                  name: other.release,
                                  values: other.p90.map((v) => (v != null ? v / 1e6 : null)),
                                  end: (() => {
                                    const last = [...other.p90].reverse().find((v) => v != null)
                                    return last != null
                                      ? `${other.release} ${mb(i18n, last)}`
                                      : undefined
                                  })(),
                                },
                              ]
                            : []
                        }
                        format={(v) => `${number(v, { maximumFractionDigits: 0 })} MB`}
                      />
                    ) : (
                      <EmptyNote title={t('perf.resources.screensNone')} />
                    )
                  })()
                )}
              </div>
            </Section>
            <Section title={t('perf.resources.warnings')} meta={t('perf.resources.warningsSub')}>
              {!rows || rows.length === 0 ? (
                <EmptyNote title={t('perf.resources.none')} />
              ) : (
                <ul className="rl pf-rl">
                  {rows.slice(0, 6).map((r) => (
                    <li key={r.release}>
                      <ReleaseLink release={r.release} />
                      <span>
                        {t('perf.resources.warningsLine', {
                          warnings: one(r.warnings_per_1k_sessions),
                          exits: one(r.out_of_memory_per_1k_sessions),
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="pf-note">
                <Link
                  className="ul"
                  to="/o/$orgId/p/$projectId/health/exits"
                  params={params}
                  search={{ app: scope.app_id }}
                >
                  {t('exits.title')}
                </Link>
              </p>
            </Section>
          </div>

          {notYet === null ? (
            <>
              <div className="grid2">
                <Section title={t('perf.resources.size')} meta={t('perf.resources.sizeSub')}>
                  {!rows || rows.every((r) => r.install_size == null && r.data_p50 == null) ? (
                    <EmptyNote title={t('perf.resources.capturedNone')}>
                      {t('perf.resources.capturedNoneBody')}
                    </EmptyNote>
                  ) : (
                    <Table
                      label={t('table.of', { name: t('perf.resources.size') })}
                      className="rs-tbl"
                    >
                      <thead>
                        <tr>
                          <th scope="col">{t('perf.resources.col.release')}</th>
                          <th className="num" scope="col">
                            {t('perf.resources.col.install')}
                          </th>
                          <th className="num" scope="col">
                            {t('perf.resources.col.change')}
                          </th>
                          <th className="num" scope="col">
                            {t('perf.resources.col.dataP50')}
                          </th>
                          <th className="num" scope="col">
                            {t('perf.resources.col.dataP90')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => {
                          const next = rows[i + 1]
                          const change =
                            r.install_size != null && next?.install_size != null
                              ? (r.install_size - next.install_size) / 1e6
                              : null
                          return (
                            <tr key={r.release}>
                              <td>
                                <ReleaseLink release={r.release} />
                              </td>
                              <td className="num">{mb(i18n, r.install_size)}</td>
                              <td className="num">
                                <Delta
                                  value={change}
                                  format={(v) => `${number(v, { maximumFractionDigits: 1 })} MB`}
                                />
                              </td>
                              <td className="num">{mb(i18n, r.data_p50)}</td>
                              <td className="num">{mb(i18n, r.data_p90)}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  )}
                </Section>
                <Section title={t('perf.resources.free')} meta={t('perf.resources.freeSub')}>
                  {!overview.data || overview.data.free_space.every((f) => f.installs === 0) ? (
                    <EmptyNote title={t('perf.resources.capturedNone')} />
                  ) : (
                    overview.data.free_space.map((f) => {
                      const tone = FREE.find(([b]) => b === f.bucket)?.[1] ?? ''
                      return (
                        <div key={f.bucket} className="rs-share">
                          <span>
                            {t(`perf.resources.freeBucket.${f.bucket as (typeof FREE)[number][0]}`)}
                          </span>
                          <span className="b">
                            <i className={tone} style={{ width: `${f.share * 100}%` }} />
                          </span>
                          <span>{percent(f.share, 1)}</span>
                        </div>
                      )
                    })
                  )}
                </Section>
              </div>

              <div className="grid-h">
                <Section title={t('perf.resources.battery')} meta={t('perf.resources.batterySub')}>
                  {battery.isPending ? (
                    <Loading />
                  ) : battery.isError ? (
                    <ErrorState error={battery.error} retry={() => battery.refetch()} />
                  ) : battery.data.screens.length === 0 ? (
                    <EmptyNote title={t('perf.resources.capturedNone')}>
                      {t('perf.resources.capturedNoneBody')}
                    </EmptyNote>
                  ) : (
                    <Table
                      label={t('table.of', { name: t('perf.resources.battery') })}
                      className="rs-tbl"
                    >
                      <thead>
                        <tr>
                          <th scope="col">{t('vitals.col.screen')}</th>
                          <th className="num" scope="col">
                            {t('perf.resources.col.median')}
                          </th>
                          <th className="num" scope="col" translate="no">
                            p90
                          </th>
                          <th className="num" scope="col">
                            {previous
                              ? t('perf.resources.col.vsShort', { release: previous })
                              : t('perf.resources.col.vsNone')}
                          </th>
                          <th className="num" scope="col">
                            {t('perf.resources.col.foreground')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {battery.data.screens.map((s) => (
                          <tr key={s.screen}>
                            <td className="mono anywhere" translate="no">
                              <Link
                                to="/o/$orgId/p/$projectId/performance/screens/detail"
                                params={params}
                                search={{ app: scope.app_id, screen: s.screen }}
                              >
                                {s.screen}
                              </Link>
                            </td>
                            <td className="num">{two(s.median)}</td>
                            <td className="num">{two(s.p90)}</td>
                            <td className="num">
                              <Delta
                                value={
                                  s.baseline_median != null ? s.median - s.baseline_median : null
                                }
                                format={two}
                              />
                            </td>
                            <td className="num">
                              {`${number(s.minutes / 60, { maximumFractionDigits: s.minutes < 600 ? 1 : 0 })} h`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Section>
                <Section
                  title={t('perf.resources.thermal')}
                  meta={t('perf.resources.thermalSub', { release: release ?? '—' })}
                >
                  {thermal.isPending ? (
                    <Loading />
                  ) : thermal.isError ? (
                    <ErrorState error={thermal.error} retry={() => thermal.refetch()} />
                  ) : (
                    <>
                      {thermal.data.snapshots === 0 ? (
                        <EmptyNote title={t('perf.resources.capturedNone')} />
                      ) : (
                        thermal.data.states.map((s) => (
                          <div key={s.state} className="rs-share">
                            <span>
                              {t(`perf.resources.${android ? 'stateAndroid' : 'state'}.${s.state}`)}
                            </span>
                            <span className="b">
                              <i
                                className={
                                  s.state === 'critical'
                                    ? 'rs-c'
                                    : s.state === 'serious'
                                      ? 'rs-w'
                                      : ''
                                }
                                style={{
                                  width: `${Math.max(s.share * 100, s.share > 0 ? 0.8 : 0)}%`,
                                }}
                              />
                            </span>
                            <span>{percent(s.share, 1)}</span>
                          </div>
                        ))
                      )}
                      <dl className="kv rs-kv">
                        <dt>
                          {t(android ? 'perf.resources.batterySaver' : 'perf.resources.lowPower')}
                        </dt>
                        <dd>
                          {thermal.data.low_power_share != null
                            ? t('perf.resources.ofSessions', {
                                share: percent(thermal.data.low_power_share, 0),
                              })
                            : '—'}
                        </dd>
                        <dt>
                          {t(
                            android
                              ? 'perf.resources.batterySaverDrain'
                              : 'perf.resources.lowPowerDrain',
                          )}
                        </dt>
                        <dd>
                          {thermal.data.low_power_drain != null
                            ? `${two(thermal.data.low_power_drain)} %/min`
                            : '—'}
                        </dd>
                        <dt>
                          {t(android ? 'perf.resources.hottestAndroid' : 'perf.resources.hottest')}
                        </dt>
                        <dd translate="no">
                          {thermal.data.hottest_screen
                            ? `${thermal.data.hottest_screen.screen} ${percent(thermal.data.hottest_screen.share, 1)}`
                            : '—'}
                        </dd>
                      </dl>
                    </>
                  )}
                </Section>
              </div>
            </>
          ) : (
            // Spec 7.7, 7.9: the app's SDK records no snapshots or storage yet (APS-462): one note
            // for the captured-only parts, and the one figure every session's summary gives.
            <div className="rs-later">
              <EmptyNote title={t('perf.resources.later', { platform: notYet })}>
                {t('perf.resources.laterBody')}
              </EmptyNote>
              {thermal.data?.low_power_share != null ? (
                <dl className="kv rs-kv">
                  <dt>{t('perf.resources.lowPower')}</dt>
                  <dd>
                    {t('perf.resources.ofSessions', {
                      share: percent(thermal.data.low_power_share, 0),
                    })}
                  </dd>
                </dl>
              ) : null}
            </div>
          )}

          <Section title={t('perf.resources.how')} minor>
            <p className="rs-note">
              {t('perf.resources.howBody')}
              {android ? ` ${t('perf.resources.howAndroid')}` : null}
              {` ${t('perf.resources.howSdk', {
                list: PLATFORMS.map((p) => {
                  const since = RESOURCES_SINCE[p]
                  return since === null
                    ? t('perf.resources.sdkNone', { platform: PLATFORM_LABEL[p] })
                    : t('perf.resources.sdkSince', { platform: PLATFORM_LABEL[p], version: since })
                }).join('; '),
              })}`}
            </p>
          </Section>
        </>
      )}
    </main>
  )
}
